// ====================================================================
//  THE EYE  —  Ambulation Zone feature
//  A photorealistic human eye: a real photographed iris (eyeiris.png) is
//  rendered as the iris disc and physically slides toward the cursor inside
//  a wet, shaded sclera framed by an almond eyelid opening. The iris photo is
//  clipped to a circle; the whole eye opening is clipped to the almond so the
//  iris can never slide out past the lids. Layered SVG gradients give the
//  sclera its rounded wet shading, a soft caruncle, faint veins and a crisp
//  specular catchlight. CSS keeps it alive: a slow wet breathing pulse and a
//  periodic skin-toned blink eyelid that sweeps down every few seconds.
//
//  Cursor tracking is handled in JS (rAF-throttled) by translating the iris
//  group; everything else is declarative SVG + CSS so it stays GPU-cheap
//  (transform / opacity only). prefers-reduced-motion holds a steady, centered
//  gaze with no blink loop and no breathing.
//
//  Self-contained. Renders ~450x350 to match the prior eye footprint.
// ====================================================================

import React, { Component } from 'react';
import { rafThrottle, prefersReducedMotion } from '../_shared/sketchFrame';
import irisUrl from '../../../images/eyeiris.png';
import './theeye.css';

// Eye geometry (SVG user units == viewBox units).
const VIEW_W = 450;
const VIEW_H = 350;
const CX = 225;        // eye center x
const CY = 175;        // eye center y
const IRIS_R = 70;     // iris photo radius
// How far the iris is allowed to travel from center, in each axis, so it stays
// comfortably inside the almond opening (never clipping the lids hard).
const TRAVEL_X = 46;
const TRAVEL_Y = 24;

class TheEye extends Component {
    constructor(props) {
        super(props);
        this.wrapRef = React.createRef();
        this.irisRef = React.createRef();   // the cursor-tracking iris group
        this._reduced = false;
        // rAF-throttled pointer handler so we touch the DOM at most once/frame.
        this._onMove = rafThrottle(this._track);
    }

    // Translate the iris group toward the cursor, clamped to the travel box so
    // the photo iris slides within the eye opening and never leaves the almond.
    _track = (clientX, clientY) => {
        const el = this.irisRef.current;
        const wrap = this.wrapRef.current;
        if (!el || !wrap) return;
        const r = wrap.getBoundingClientRect();
        if (!r.width || !r.height) return;

        // Pointer position in viewBox space.
        const vx = ((clientX - r.left) / r.width) * VIEW_W;
        const vy = ((clientY - r.top) / r.height) * VIEW_H;

        // Vector from eye center to pointer, softened then clamped to an
        // ellipse so motion eases near the edges rather than slamming a corner.
        let dx = vx - CX;
        let dy = vy - CY;
        const soft = 0.42;            // gaze does not chase 1:1; feels organic
        dx *= soft;
        dy *= soft;
        // Clamp to the travel ellipse.
        const nx = dx / TRAVEL_X;
        const ny = dy / TRAVEL_Y;
        const m = Math.hypot(nx, ny);
        if (m > 1) { dx /= m; dy /= m; }

        el.style.transform = `translate(${dx.toFixed(2)}px, ${dy.toFixed(2)}px)`;
    };

    _handlePointer = (e) => {
        this._onMove(e.clientX, e.clientY);
    };

    componentDidMount() {
        this._reduced = prefersReducedMotion();
        // Steady, centered gaze under reduced motion: no tracking listener.
        if (!this._reduced && typeof window !== 'undefined') {
            window.addEventListener('mousemove', this._handlePointer, { passive: true });
        }
    }

    componentWillUnmount() {
        if (typeof window !== 'undefined') {
            window.removeEventListener('mousemove', this._handlePointer);
        }
        if (this._onMove && this._onMove.cancel) this._onMove.cancel();
    }

    render() {
        return (
            <div ref={this.wrapRef} className="theeye-wrap">
                <svg
                    className="theeye-svg"
                    viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                    width={VIEW_W}
                    height={VIEW_H}
                    role="img"
                    aria-label="A photorealistic eye that follows your cursor"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <defs>
                        {/* The almond eye opening — clips everything wet to the lids. */}
                        <clipPath id="eye-almond" clipPathUnits="userSpaceOnUse">
                            <path d="
                                M 65 175
                                C 120 95, 330 95, 385 175
                                C 330 255, 120 255, 65 175
                                Z" />
                        </clipPath>

                        {/* Circular clip so the rectangular iris photo reads as a disc. */}
                        <clipPath id="iris-circle" clipPathUnits="userSpaceOnUse">
                            <circle cx={CX} cy={CY} r={IRIS_R} />
                        </clipPath>

                        {/* Sclera body: warm-white, shaded darker toward the upper
                            inner corner for eyeball curvature. */}
                        <radialGradient id="sclera-shade" cx="50%" cy="38%" r="72%">
                            <stop offset="0%" stopColor="#fdfbf8" />
                            <stop offset="62%" stopColor="#f3ece3" />
                            <stop offset="100%" stopColor="#d8c8b6" />
                        </radialGradient>

                        {/* Top crease shadow where the upper lid meets the eyeball. */}
                        <linearGradient id="upper-shade" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#5a463c" stopOpacity="0.55" />
                            <stop offset="38%" stopColor="#5a463c" stopOpacity="0" />
                        </linearGradient>

                        {/* Wet glassy dome over the whole iris/sclera. */}
                        <radialGradient id="wet-dome" cx="42%" cy="32%" r="68%">
                            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.42" />
                            <stop offset="32%" stopColor="#ffffff" stopOpacity="0.05" />
                            <stop offset="100%" stopColor="#000000" stopOpacity="0.18" />
                        </radialGradient>

                        {/* Subtle vignette darkening the sclera into the lids. */}
                        <radialGradient id="sclera-vignette" cx="50%" cy="50%" r="60%">
                            <stop offset="0%" stopColor="#000000" stopOpacity="0" />
                            <stop offset="78%" stopColor="#000000" stopOpacity="0" />
                            <stop offset="100%" stopColor="#3a2c20" stopOpacity="0.45" />
                        </radialGradient>

                        {/* Skin gradient for the blink lid. */}
                        <linearGradient id="lid-skin" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#d9a987" />
                            <stop offset="55%" stopColor="#e7bd9b" />
                            <stop offset="100%" stopColor="#cf9876" />
                        </linearGradient>
                    </defs>

                    {/* ===== EYE OPENING (everything wet clipped to the almond) ===== */}
                    <g clipPath="url(#eye-almond)">
                        {/* breathing wrapper — a slow wet pulse scales the eyeball a hair */}
                        <g className="theeye-breathe">
                            {/* sclera base */}
                            <rect x="40" y="60" width="370" height="230" fill="url(#sclera-shade)" />

                            {/* warm caruncle hint in the inner corner */}
                            <ellipse cx="80" cy="182" rx="16" ry="11" fill="#d88e8e" opacity="0.55" />

                            {/* faint branching veins crawling from the corners */}
                            <g stroke="#be4a44" strokeWidth="1.1" fill="none"
                               strokeLinecap="round" opacity="0.5">
                                <path d="M 70 168 q 26 -10 54 -4 q 18 4 30 -2" />
                                <path d="M 96 196 q 30 8 58 2" />
                                <path d="M 388 178 q -28 -12 -56 -6 q -14 3 -24 -2" />
                                <path d="M 372 198 q -30 10 -60 4" />
                                <path d="M 74 160 q 10 -8 22 -7" strokeWidth="0.8" opacity="0.4" />
                            </g>

                            {/* ===== IRIS — the photo, cursor-tracked ===== */}
                            {/* This group is translated by JS toward the cursor.
                                The image is clipped to a circle; a shadow ring and
                                limbal ring sell it as a wet eyeball. */}
                            <g ref={this.irisRef} className="theeye-iris">
                                {/* contact shadow under the iris dome */}
                                <ellipse cx={CX} cy={CY + 3} rx={IRIS_R + 4} ry={IRIS_R + 4}
                                         fill="#000000" opacity="0.22" />
                                {/* the real photographed iris, masked to a disc */}
                                <image
                                    href={irisUrl}
                                    xlinkHref={irisUrl}
                                    x={CX - IRIS_R}
                                    y={CY - IRIS_R}
                                    width={IRIS_R * 2}
                                    height={IRIS_R * 2}
                                    clipPath="url(#iris-circle)"
                                    preserveAspectRatio="xMidYMid slice"
                                />
                                {/* dark limbal ring crisps the iris rim */}
                                <circle cx={CX} cy={CY} r={IRIS_R - 1}
                                        fill="none" stroke="#161013" strokeWidth="3"
                                        opacity="0.65" />
                                {/* inner shadow from the iris into the pupil for depth */}
                                <circle cx={CX} cy={CY} r={IRIS_R * 0.34}
                                        fill="#000000" opacity="0.0" />
                                {/* crisp specular catchlight (travels with the iris) */}
                                <ellipse cx={CX - 22} cy={CY - 26} rx="9" ry="11"
                                         fill="#ffffff" opacity="0.9" className="theeye-glint" />
                                <circle cx={CX + 16} cy={CY + 20} r="3.5"
                                        fill="#ffffff" opacity="0.45" />
                            </g>

                            {/* top crease shadow (above the iris, fixed to the lid) */}
                            <rect x="40" y="60" width="370" height="120" fill="url(#upper-shade)" />
                            {/* wet glassy dome highlight over everything */}
                            <rect x="40" y="60" width="370" height="230" fill="url(#wet-dome)" />
                            {/* vignette into the lids */}
                            <rect x="40" y="60" width="370" height="230" fill="url(#sclera-vignette)" />
                        </g>

                        {/* ===== BLINK LID — skin sweeps down over the eye ===== */}
                        {/* Sized to cover the whole opening; CSS scales it down from
                            the top on a periodic loop. transform-origin at the top. */}
                        <g className="theeye-blink">
                            <rect x="40" y="55" width="370" height="125" fill="url(#lid-skin)" />
                            {/* lash line at the lid's leading edge */}
                            <rect x="40" y="176" width="370" height="4" fill="#3a241b" opacity="0.8" />
                        </g>
                    </g>

                    {/* ===== ALMOND LID FRAME (ink rim + lashes) ===== */}
                    <path d="
                        M 65 175
                        C 120 95, 330 95, 385 175
                        C 330 255, 120 255, 65 175
                        Z"
                        fill="none" stroke="#2a1d16" strokeWidth="3" />
                    {/* upper crease line above the lid */}
                    <path d="M 96 150 C 160 108, 300 108, 356 152"
                          fill="none" stroke="#6a4f42" strokeWidth="5"
                          strokeLinecap="round" opacity="0.45" />
                    {/* a few upper lashes curling out */}
                    <g stroke="#1c1310" strokeWidth="2" fill="none" strokeLinecap="round">
                        <path d="M 150 118 q -4 -10 -10 -16" />
                        <path d="M 200 109 q -2 -11 -6 -18" />
                        <path d="M 255 109 q 2 -11 7 -18" />
                        <path d="M 305 117 q 5 -10 12 -15" />
                    </g>
                </svg>
            </div>
        );
    }
}

export default TheEye;
